/**
 * Every word the CAD1 pages show (PLAN cad1-phase3 §6 S1; Bez 2026-10-09: "1, 2, 3 -> launch", "it is not a newspaper").
 *
 * The page shows only step titles, field labels, one number per step, the four must-see facts of the launch step as short labels, buttons,
 * and status or error labels of six words or fewer. Every explanation, even one sentence, and every link lives in a hint (the (i)) or in
 * the Details sheet; nothing here is deleted, only moved. Human units on the page: "10 years", dates like "Oct 5, 2036", compact amounts
 * ("9.9M", "≈ $5.6K"), whole percents ("< 1 %"); raw units (wei, seconds, ticks, salt) and function names only in the raw call inside
 * Details. The one exact amount on the page is "You pay": the BNB the wallet signs. A hint is a title, a sentence or two and at most two
 * links; a longer record (a list, several facts) goes to Details. Each fact is said once per sheet where it can be (audit 2026-10-09).
 *
 * copy.test.ts walks this module and the JSX text of views/Cad1 for banned words and checks the live-figure rules:
 *   - "No creation fee" and "Your first BNB buys your token" only while the lock price reads exactly 0;
 *   - the start value is "about", never exact, and it is the start price × the whole supply, never money in the pool (review H1);
 *   - the fee tier comes from tierStatus().fee (passed in), never a constant;
 *   - the fee split names the protocol's share (the default "about a third", or the pool's live read); the rest goes to whoever provides
 *     liquidity in range, the creator's locked position included, never "the creator earns two-thirds" (review H2).
 * Money is bigint all the way (logic/quote.ts formats it); nothing here reads the chain.
 */
import { CAD1_LAUNCHER_SHA } from '../abi/cad1Launcher'
import { CAD1_PLANNED } from '../config'
import { formatBnb, formatBnbExact, formatShareBps, formatTokens } from './quote'

export const CAD1_SUPPORT_URL = 'https://t.me/cadinulover'
export const CAD1_SUPPORT_HANDLE = '@cadinulover'

const group = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
const tenthsText = (tenths: bigint) => `${group((tenths / 10n).toString())}${tenths % 10n ? `.${tenths % 10n}` : ''}`

/** wei → "9.9" (one decimal, truncated) below 100 BNB, whole BNB above. */
export function bnbTenths(wei: bigint): string {
  if (wei >= 100n * 10n ** 18n) return group((wei / 10n ** 18n).toString())
  const t = wei / 10n ** 17n
  return `${t / 10n}.${t % 10n}`
}

/** wei → BNB with at most two decimals, for people: "2.49" (truncated), or rounded up with `up` (an amount someone still needs). */
export function bnbShort(wei: bigint, up = false): string {
  if (wei <= 0n) return '0'
  const unit = 10n ** 16n
  const hundredths = up ? (wei + unit - 1n) / unit : wei / unit
  if (hundredths === 0n) return '< 0.01'
  const frac = (hundredths % 100n).toString().padStart(2, '0').replace(/0+$/, '')
  return `${group((hundredths / 100n).toString())}${frac ? `.${frac}` : ''}`
}

/** A token amount (18 decimals) for people: "9.9M", "950K", "1.2B", "12.5"; one decimal, truncated, so it never reads high. */
export function compactAmount(wei: bigint): string {
  if (wei <= 0n) return '0'
  const units: Array<[bigint, string]> = [
    [10n ** 27n, 'B'],
    [10n ** 24n, 'M'],
    [10n ** 21n, 'K'],
  ]
  for (const [unit, suffix] of units) {
    if (wei >= unit) {
      const tenths = (wei * 10n) / unit
      return tenths >= 1000n ? `${group((tenths / 10n).toString())}${suffix}` : `${tenthsText(tenths)}${suffix}`
    }
  }
  const tenths = (wei * 10n) / 10n ** 18n
  return tenths === 0n ? '< 0.1' : tenthsText(tenths)
}

/** "$5,900" (logic/usd.ts, two significant figures) → "$5.9K"; small amounts and "< $0.01" stay as they are; null stays null. */
export function compactUsd(usd: string | null | undefined): string | null {
  if (!usd) return null
  const m = /^\$([\d,]+)$/.exec(usd)
  if (!m) return usd
  const n = Number(m[1].replace(/,/g, ''))
  if (!Number.isFinite(n) || n < 1000) return usd
  const [unit, suffix] = n >= 1e9 ? [1e9, 'B'] : n >= 1e6 ? [1e6, 'M'] : [1e3, 'K']
  const v = n / unit
  return `$${v >= 100 ? Math.floor(v) : Math.floor(v * 10) / 10}${suffix}`
}

/** A share of the supply as a whole percent, truncated (1,999 bps reads 19 %, never 20 %); under 1 % reads "< 1 %". */
export const wholePercent = (bps: bigint): string => (bps <= 0n ? '0 %' : bps < 100n ? '< 1 %' : `${bps / 100n} %`)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** The UTC day of a unix time: "Oct 5, 2036". */
export const humanDate = (seconds: bigint | number): string => {
  const d = new Date(Number(seconds) * 1000)
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`
}
/** The viewer's local time of a unix time: "4:20 PM" (a no-break space keeps PM with its time). */
export const humanTime = (seconds: bigint | number): string => {
  const d = new Date(Number(seconds) * 1000)
  const h = d.getHours()
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}\u00a0${h < 12 ? 'AM' : 'PM'}`
}
/** "Oct 5, 2036, 18:52 UTC": the full moment, for the Details sheet only. */
export const humanUtc = (seconds: bigint | number): string => `${humanDate(seconds)}, ${new Date(Number(seconds) * 1000).toISOString().slice(11, 16)} UTC`

/** "0.5 %" from the tier fee in hundredths of a bip (tierStatus().fee); "—" when it was not read. */
export function feePercent(fee: number | undefined | null): string {
  if (typeof fee !== 'number' || !Number.isInteger(fee) || fee < 0) return '—'
  const whole = Math.floor(fee / 10_000)
  const frac = String(fee % 10_000).padStart(4, '0').replace(/0+$/, '')
  return `${whole}${frac ? `.${frac}` : ''} %`
}

/** A price far below 0.0001 BNB: "0.00000000991 BNB" (three significant figures, truncated). */
export function formatTinyBnb(wei: bigint): string {
  if (wei <= 0n) return '0'
  if (wei >= 10n ** 14n) return formatBnb(wei)
  const s = wei.toString().padStart(18, '0')
  const lead = s.search(/[1-9]/)
  return `0.${s.slice(0, lead)}${s.slice(lead, lead + 3).replace(/0+$/, '')}`
}

/** A pool's feeProtocol (token0 low 16 bits, token1 high 16 bits, each /10,000) → the protocol's share of the pool fee per side, as "32 %". */
export function protocolShareOfFee(feeProtocol: number): { low: string; high: string } {
  const p0 = feeProtocol % 65_536
  const p1 = Math.floor(feeProtocol / 65_536) % 65_536
  const pct = (p: number) => {
    const c = Math.min(10_000, Math.max(0, p))
    const frac = String(c % 100).padStart(2, '0').replace(/0+$/, '')
    return `${Math.floor(c / 100)}${frac ? `.${frac}` : ''} %`
  }
  return { low: pct(Math.min(p0, p1)), high: pct(Math.max(p0, p1)) }
}

/**
 * Who gets the pool fee (review H2). The fact is the protocol's share (feeProtocol, set per pool by the DEX operator); the rest goes to
 * the liquidity in range: the creator's locked position, plus anyone else who adds liquidity to the 0.5 % pool. So no line promises the
 * creator a share. Without a live read of the pool: the default, said as a default.
 */
export function feeShareLine(feeProtocol?: number | null, fee?: string): string {
  if (typeof feeProtocol !== 'number' || !Number.isInteger(feeProtocol) || feeProtocol < 0) return 'The protocol takes about a third of the pool fee by default; liquidity providers share the rest.'
  const of = fee && fee !== '—' ? `the ${fee} fee` : 'the pool fee'
  if (feeProtocol === 0) return `The protocol takes nothing from ${of} in this pool now (read live); liquidity providers share all of it.`
  const { low, high } = protocolShareOfFee(feeProtocol)
  return `The protocol takes ${low === high ? low : `${low} to ${high}`} of ${of} in this pool (read live); liquidity providers share the rest.`
}

/** The start value, always approximate: "about 9.9 BNB (≈ $5,900)"; "—" when it cannot be read. */
export function startValueLine(startCapReal: bigint | null | undefined, usd?: string | null): string {
  if (typeof startCapReal !== 'bigint' || startCapReal <= 0n) return '—'
  return `about ${bnbTenths(startCapReal)} BNB${usd ? ` (≈ ${usd})` : ''}`
}

export type LockFeeWords = { tile: string; line: string | null; freeLines: boolean }

/** The lock fee: free words only while the live read is exactly 0 BNB; otherwise the price and who it is paid to. */
export function lockFeeWords(lockFee: bigint | undefined | null): LockFeeWords {
  if (lockFee === 0n) return { tile: 'Lock fee 0 BNB', line: 'No lock fee', freeLines: true }
  if (typeof lockFee === 'bigint' && lockFee > 0n) return { tile: `Lock service fee ${formatBnb(lockFee)} BNB`, line: `Lock service fee ${formatBnb(lockFee)} BNB, paid to the DEX operator`, freeLines: false }
  return { tile: 'Lock fee —', line: null, freeLines: false }
}

export type CreationFeeWords = { tile: string; line: string | null }

/**
 * v2's creation fee (Bez 2026-10-09: 0.005 BNB for now), read live from the launcher (planned in preview): every launch pays it to CADINU.
 * "No creation fee" only while it reads exactly 0; unread, no figure and no claim.
 */
export function creationFeeWords(fee: bigint | undefined | null): CreationFeeWords {
  if (fee === 0n) return { tile: 'Creation fee 0 BNB', line: 'No creation fee' }
  if (typeof fee === 'bigint' && fee > 0n) return { tile: `Creation fee ${formatBnbExact(fee)} BNB`, line: `Creation fee ${formatBnbExact(fee)} BNB, paid to CADINU` }
  return { tile: 'Creation fee not read yet', line: null }
}

/** For display only: a no-break space before "%", so a figure never wraps away from its unit (review L-low). Copy itself keeps plain
 * spaces, so tests and the banned-word scan read it as written. */
export const keepUnits = (s: string): string => s.replace(/ %/g, '\u00a0%')

const utc = (seconds: bigint | number) => `${new Date(Number(seconds) * 1000).toISOString().slice(0, 16).replace('T', ' ')} UTC`
const year = (seconds: bigint | number) => new Date(Number(seconds) * 1000).getUTCFullYear()
export const fmtUtc = utc

/** The lock term in years, from the launcher's constant (315,360,000 s = 10 × 365 days). */
export const LOCK_YEARS = CAD1_PLANNED.lockDuration / 31_536_000n
const LOCK_TERM = `${LOCK_YEARS} years`

export const OPERATOR_SENTENCE =
  'The DEX operator can set the protocol share of the fee between 0 and 40 %, attach a farm module to the pool and halt trading in the pool. Nobody can open the lock before its date, including CADINU.'

export const COPY = {
  brand: 'CAD1',
  previewTag: 'Preview',
  previewInfo: "Estimate: the launcher isn't deployed yet, so figures come from its code. Live numbers replace them.",
  /** the only place the launcher commit appears: the Details sheet, in preview */
  provenance: `Preview figures come from the launcher code (version ${CAD1_LAUNCHER_SHA.slice(0, 7)}).`,
  highShare: 'High creator share',
  highShareInfo: 'At 20 % of the supply or more, everyone sees this label on the token.',
  notVerified: 'not verified',
  notVerifiedInfo: "CADINU doesn't review launches. Check the token yourself.",
  dash: '—',
  retry: 'Retry',
  switchChain: 'Switch to BNB Chain',
  connect: 'Connect wallet',
  copy: 'Copy',
  close: 'Close',
  details: 'Details',
  support: 'Telegram support',
  allRisks: 'All risks',
  lockTerm: LOCK_TERM,
  steps: ['Name', 'Buy', 'Launch'],

  entry: {
    h1: 'Launch your token in 3 steps',
    howTitle: 'How it works',
    how: `One transaction creates your token, its CADINUdex pool and a ${LOCK_YEARS}-year liquidity lock. CADINU doesn't review launches; you can lose what you put in.`,
    start: 'Start',
    seeLaunches: 'See launches',
    detailsTitle: 'CAD1 in detail',
    detailsHide: 'Hide details',
    costTitle: 'What it costs',
    creationFeeNone: 'Free',
    firstBuyTitle: 'First buy',
    firstBuyAtLeast: (bnb: string) => `At least ${bnb} BNB`,
    firstBuyAny: 'Any amount, or none',
    firstBuyInfo: "Your first buy goes into your token's pool and buys your token. It does not go to CADINU.",
    lockOneLine: "The pool's liquidity is locked for the whole term. CADINU does not review launches.",
    startValue: 'Start value',
    creationFee: 'Creation fee',
    creationFeeInfo:
      'Every launch pays it to CADINU, on top of the first buy and the lock fee; it keeps spam launches away. Read live from the launcher: CADINU can change it for later launches, and the launch step shows it again before you sign.',
    lockFeeTitle: 'Lock fee',
    serviceFee: 'Lock service fee',
    paidOperator: 'paid to the DEX operator',
    tradingFee: 'Trading fee',
    lockTitle: 'Liquidity locked',
    lockValue: `${LOCK_TERM}, owned by the creator`,
    noCurve: 'No bonding curve. No extra trading tax. No max buy · No max wallet · No max transaction.',
    /** only while the lock fee reads 0: the pool fee still comes off the first buy, and part of it goes to the protocol (review H3) */
    firstBnb: (fee: string) => `Your first BNB buys your token. Only the ${fee} pool fee comes off it; about a third of that goes to the CADINU protocol.`,
    railTitle: 'How it works',
    railCaption: 'All of it happens, or none of it.',
    rail: ['Token at a …CAD1 address', 'Pool on CADINUdex at the start price', 'Whole supply in one position', 'Your first buy, into the pool (not to CADINU)', `Lock for ${LOCK_TERM}, owned by you`, 'Trading opens'],
    provenTitle: 'What is proven, and how to check it',
    canTitle: 'Who can do what',
    risksTitle: 'All risks',
    tierLabel: (fee: string, ready: boolean) => `${fee === '—' ? 'CAD1' : fee} tier: ${ready ? 'set up' : 'not set up yet'}`,
    tierUnknown: (fee: string) => `${fee === '—' ? 'CAD1' : fee} tier: not read yet`,
    checkAfterDeploy: 'Checkable on BscScan after deployment.',
  },

  /** status labels (six words or fewer) and the sentence each one's hint holds */
  states: {
    paused: 'Launches paused',
    pausedInfo: 'New launches are paused. Tokens already launched are not affected.',
    closed: 'Launches closed',
    closedInfo: 'Launches are closed right now. Tokens already launched are not affected.',
    misconfigured: 'Not set up yet',
    misconfiguredInfo: "The launcher's setup does not match CADINUdex, so launches are off. Tokens already launched are not affected.",
    unreachable: "Can't reach BNB Chain",
    unreachableInfo: "Couldn't read BNB Chain just now. The form stays read-only until it answers.",
    poolNotRestricted: 'Pool not restricted',
    poolNotRestrictedInfo: 'Someone could set up your pool first; your launch would then be refused, and only the network fee would be spent.',
    loading: 'Reading BNB Chain',
  },

  /** the Details sheet's longer lines */
  info: {
    startLive: (startCap: bigint, startCapReal: bigint, at: number) =>
      `Set to ${formatBnb(startCap)} BNB. At the start price the whole supply (1,000,000,000) is valued at about ${formatBnb(startCapReal)} BNB, because prices sit on fixed steps. The pool holds no BNB at launch except your first buy. Read from the CAD1 launcher at ${utc(Math.floor(at / 1000))}.`,
    startPreview: (startCapReal: bigint) =>
      `Planned: 10 BNB. At the start price the whole supply (1,000,000,000) would be valued at about ${formatBnb(startCapReal)} BNB, because prices sit on fixed steps. The pool would hold no BNB at launch except the first buy.`,
    lockFee: 'Read live from the CADINU Lock. Its admin can change the price for new locks, so it is read again at the launch step and right before you sign.',
    /** the tier only: the split is the fee line beside it (feeShareLine), the operator's powers are "Who can do what" (audit 2026-10-09) */
    trading: (fee: string, live: boolean) => `${fee} is the CAD1 fee tier${live ? ', read from the launcher' : ' planned for the launcher'}.`,
    lockDays: `${LOCK_TERM}, a constant of the launcher. The lock owner can extend it, never shorten it.`,
  },

  can: {
    you: { who: 'You, the creator', can: ['Collect the trading fees of your position', 'Extend the lock', 'Transfer the lock to another wallet'], cannot: ['Shorten the lock', 'Withdraw the liquidity before the unlock date'] },
    operator: {
      who: 'The DEX operator',
      can: ['Set the protocol share of the fee between 0 and 40 %', 'Attach a farm module to the pool', 'Halt trading in the pool', 'Stop new launches on the CAD1 fee tier'],
      cannot: ['Open, move or shrink the lock'],
    },
    cadinu: {
      who: 'CADINU, the launcher owner',
      can: ['Change the start value for future launches, within 5–50 BNB', 'Pause new launches'],
      cannot: ['Change a token after its launch: it has no owner and no mint', 'Open the lock'],
    },
    canLabel: 'Can',
    cannotLabel: 'Cannot',
    readLive: 'Owner read live from BNB Chain',
    notDeployed: 'Launcher not deployed yet',
  },

  proven: [
    { claim: 'The liquidity is locked in the CADINU Lock until its date. Anyone can check it.', link: 'Lock contract on BscScan' },
    { claim: 'The token code: no owner · no tax · no mint · no blacklist · no pause.', link: 'Launcher on BscScan' },
    { claim: 'Only tokens in the launcher registry are CAD1 launches.', link: 'See the registry' },
    { claim: 'Your first buy is part of the launch transaction: nobody can buy before you. Bots can buy right after.', link: null },
  ],

  risks: [
    'Anyone can launch a token here. CADINU does not check projects. A CAD1 token is not reviewed, endorsed or vetted by CADINU.',
    'Token prices can fall to near zero. You can lose everything you put in. Nothing on this page is advice or a price promise.',
    'The creator may buy part of the supply at launch and can sell it at any time. Locked liquidity does not stop anyone from selling.',
    "Bots can buy in the same block, right after the launch. CAD1 has no rules against bots. Only the creator's first buy comes before everyone else, because it is part of the launch transaction.",
    'The pool starts thin: early trades move the price a lot.',
    'Every trade in the CAD1 pool pays the pool fee. About a third goes to the CADINU protocol by default; the rest goes to the liquidity providers in range: the lock owner, plus anyone who adds liquidity to the pool.',
    OPERATOR_SENTENCE,
    `The liquidity is locked for ${LOCK_TERM}. After that date the lock owner can withdraw it. The lock owner can extend the lock but never shorten it, and can transfer the lock to another wallet.`,
    'No external audit. The CADINU team tested the contracts on a copy of BNB Chain (fork tests, fuzzing and internal adversarial reviews).',
    "The …CAD1 address ending is a brand mark, not proof. Anyone can make an address that ends in CAD1. Only the CAD1 launcher's registry shows a real CAD1 launch.",
    'A launch cannot be undone. The token, its pool and its lock stay on BNB Chain, and the name and symbol can never change. CADINU can only hide a token from its own pages.',
    'Others can copy your name and symbol, even from your pending transaction. Your token is the one at your address and launch number.',
    'If the lock fee or the start value changes before your transaction lands, the launch is refused and only the network fee is spent. The network fee is paid even when a transaction is refused.',
    'Anyone can create other pools for a CAD1 token, on other fee tiers or DEXes. Only the CAD1 pool is the launch pool, and its price is the one shown here.',
    'You are responsible for your token and for following the laws where you live. CAD1 is a tool, not advice.',
  ],

  /** step 1 "Name" and step 2 "Buy" */
  create: {
    nameTitle: 'Name your token',
    nameInfoTitle: 'Name and symbol',
    nameInfo: 'Name and symbol are written on chain and can never change.',
    name: 'Name',
    namePlaceholder: 'Rocket Dog',
    symbol: 'Symbol',
    symbolPlaceholder: 'ROCKET',
    /** the limit lives in the accessible name, not on the page */
    fieldAria: (label: string, max: number) => `${label}, up to ${max} characters`,
    fieldErrors: {
      empty: 'Required',
      tooLong: 'Too long',
      chars: 'Letters and digits only',
      spaces: 'Single spaces only',
    } as Record<'empty' | 'tooLong' | 'chars' | 'spaces', string>,
    claim: (word: string) => `No "${word}" in names`,
    addLogo: 'Add logo',
    removeLogo: 'Remove logo',
    logoBad: 'File not allowed',
    logoRulesTitle: 'Logo file',
    logoRules: 'PNG, JPEG or WebP, up to 256 KB, 128 to 1024 px.',
    logoNotKept: 'Logo not kept',
    logoNotKeptInfo: 'Only its fingerprint was kept in this browser. Choose the logo again.',
    more: 'Add description and links',
    moreInfoTitle: 'Description and links',
    moreInfo: 'Shown on the token page only, not written on chain. Changing them finds a new …CAD1 address.',
    description: 'Description',
    website: 'Website',
    x: 'X',
    telegram: 'Telegram',
    descriptionShort: 'Min 20 characters',
    descriptionLong: 'Max 2,000 characters',
    websiteBad: 'Use a full https:// address',
    handleBad: 'Letters, numbers, _ only',
    draft: 'Draft restored',
    draftFresh: 'Start fresh',
    draftSentLabel: 'Already sent',
    draftSentTitle: 'Draft already sent',
    draftSent: (tx: string) => `This draft was already sent in transaction ${tx}. Open it, or start fresh to launch another token.`,
    openSent: 'Open it',
    buyTitle: 'First buy',
    buyInfo: (threshold: bigint | null, minimum: bigint = 0n) =>
      `${minimum > 0n ? `At least ${formatBnbExact(minimum)} BNB.` : 'Optional.'} It goes into your token's pool and buys your token, not to CADINU. You buy before anyone else; bots can buy right after.${
        threshold ? ` Above ≈ ${bnbShort(threshold)} BNB your share passes 20 % and the token shows High creator share.` : ''
      }`,
    unit: 'BNB',
    buyNone: 'None',
    buyCustom: 'Other',
    amount: 'Amount',
    buyChoose: 'Choose an amount',
    buyCustomEmpty: 'Type an amount, or None',
    /** a minimum first buy above 0 (CADINU's setting; 0 today): there is no None */
    buyCustomEmptyMin: (minimum: bigint) => `Type at least ${formatBnbExact(minimum)} BNB`,
    buyMin: (minimum: bigint) => `Min ${formatBnbExact(minimum)} BNB`,
    /** v2 (Bez 2026-10-09): every launch pays the creation fee, read live; the Buy step always shows it */
    creationFee: (fee: bigint) => `Creation fee ${formatBnbExact(fee)} BNB`,
    creationFeeUnknown: 'Creation fee not read yet',
    creationFeeInfo:
      'Every launch pays it to CADINU, on top of your first buy and the lock fee; it keeps spam launches away. CADINU can change it for later launches; the launch step reads it again before you sign, and a launch is refused rather than charged a fee you did not see.',
    buyFormat: 'Numbers only',
    getTitle: 'Your tokens',
    youGet: (tokensOut: bigint, symbol: string) => `≈ ${compactAmount(tokensOut)} $${symbol || 'SYM'}`,
    youGetNone: (symbol: string) => `0 $${symbol || 'SYM'}`,
    youGetUnset: (symbol: string) => `— $${symbol || 'SYM'}`,
    youGetInfo: (tokensOut: bigint, symbol: string, bps: bigint, usd: string | null) => `≈ ${formatTokens(tokensOut)} $${symbol || 'SYM'} · ${formatShareBps(bps)} of supply${usd ? ` · ≈ ${usd}` : ''}.`,
    noBuy: 'You start with 0 tokens. The whole supply is in the pool.',
    short: (missing: bigint) => `Need ≈ ${bnbShort(missing, true)} BNB more`,
    shortInfo: 'Plus the network fee.',
    stale: 'Timed out. Check again.',
    staleInfo: 'The launch step sat open too long, so its figures are read again before you sign.',
    remine: 'Wallet changed. Finding new address',
    newAddress: 'Finding a new address',
    remineInfo: 'Your …CAD1 address is made for one wallet and one name. A new one is being found; the launch step opens again once it is found.',
    finding: 'Finding your CAD1 address…',
    addressInfoTitle: 'Your …CAD1 address',
    addressInfo: 'Your token address ends in CAD1, as BscScan and wallets show it. Your browser searches for one that fits your wallet and name.',
    found: 'Address found',
    searchFailed: 'Address search failed',
    searchUnsupported: "This browser can't search",
    searchUnsupportedInfo: 'This browser cannot run the address search. Try another browser.',
    quoteLoading: 'Getting a quote',
    quoteFailed: 'No quote right now',
    connectFirst: 'Connect your wallet first',
    cancel: 'Cancel',
    next: 'Next',
    back: 'Back',
  },

  /** step 3 "Launch": the four facts on the page, the full record in its Details sheet */
  review: {
    title: (symbol: string) => `Launch $${symbol || 'SYM'}`,
    pay: 'You pay',
    networkSuffix: '+ network fee',
    payInfo: (firstBuy: bigint, lockFee: bigint, creationFee: bigint, gas: bigint | null | undefined) =>
      `${formatBnbExact(firstBuy)} BNB first buy + ${formatBnbExact(lockFee)} BNB lock fee + ${formatBnbExact(creationFee)} BNB creation fee. ${
        gas ? `The network fee (≈ ${formatBnb(gas)} BNB) goes to BNB Chain, not to CADINU.` : 'Your wallet shows the network fee; it goes to BNB Chain, not to CADINU.'
      }`,
    get: 'You get',
    getInfo: (tokensOut: bigint, symbol: string, bps: bigint, minOut: bigint) =>
      `≈ ${formatTokens(tokensOut)} $${symbol || 'SYM'}, ${formatShareBps(bps)} of supply. At least ${formatTokens(minOut)}, or BNB Chain refuses the launch and only the network fee is spent.`,
    getNone: 'No first buy: you start with 0 tokens, and the whole supply goes into the pool.',
    locked: 'Liquidity locked',
    lockInfoTitle: 'Liquidity lock',
    lockInfo: (unlockAt: bigint) => `Locked in the CADINU Lock until ${humanDate(unlockAt)}, owned by your wallet. You can extend it, never shorten it, and you collect its trading fees.`,
    lockLink: 'Lock contract',
    tick: "I understand it can't be undone",
    tickInfoTitle: "It can't be undone",
    tickInfo: "The token, its pool and its lock can never be deleted. CADINU doesn't review launches; you can lose what you put in.",
    back: 'Back',
    launch: 'Launch',
    notOpen: 'Not open yet',
    notOpenInfo: 'Launch opens when the launcher is deployed. Nothing can be signed in preview.',
    checking: 'Checking with BNB Chain',
    wallet: 'Confirm in your wallet',
    changed: 'Something changed. Tick again.',
    changedLive: 'Something changed since this step opened. Check the new values, then tick the box again.',
    refused: 'Launch refused',
    cancelled: 'You cancelled',
    noBnbLabel: 'Not enough BNB',
    wrongChain: 'Wrong network',
    whyTitle: 'Why',
    reviewLoading: 'Reading BNB Chain',
    reviewFailed: "Can't read BNB Chain",
    reviewFailedInfo: "Couldn't read the launch figures from BNB Chain.",
    tryAgain: 'Try again',
    checkAgain: 'Check again',
    findNew: 'Find a new address',

    /* the Details sheet */
    detailsTitle: 'Launch details',
    call: (value: string, launcher: string) => `Your wallet will ask you to send ${value} BNB to CAD1 Launcher ${launcher} (createToken) on BNB Chain.`,
    callPreview: 'In preview there is no launcher to send to. Every figure below is computed from the launcher code.',
    token: 'Your token',
    supply: 'Supply',
    supplyValue: '1,000,000,000',
    template: 'no owner · no tax · no mint · no blacklist · no pause',
    addressPreview: '0x…CAD1, found after deployment',
    addressLater: '0x…CAD1, found when launches are open',
    pool: 'Pool',
    poolLine: (symbol: string, fee: string) => `CADINUdex V3, ${symbol}/WBNB, ${fee} fee`,
    startValue: 'Start value',
    startPrice: 'Start price',
    perToken: 'BNB per token',
    dust: 'The whole supply goes into one position from the start price upward; a tiny rounding remainder is sent to the burn address.',
    poolNoBnb: 'At launch the pool holds no BNB except your first buy. The start value is the start price × the whole supply, not money in the pool.',
    buy: 'Your first buy',
    atLeast: 'at least',
    threshold: (threshold: bigint) => `Above ≈ ${bnbShort(threshold)} BNB the creator share passes 20 %.`,
    lock: 'Lock',
    lockLine: 'CADINU Lock V3 (0x9626…0Bc0)',
    lockOwner: 'Owner: your wallet',
    unlocks: (at: bigint) => `${LOCK_TERM} · unlocks ${humanUtc(at)}`,
    lockFee: 'Lock fee',
    creationFee: 'Creation fee',
    creationFeeLine: (fee: bigint) => `${formatBnbExact(fee)} BNB, paid to CADINU`,
    money: 'Money',
    /** exact decimals: the line says exactly what msg.value carries (review M7); v2 adds the creation fee */
    moneyLine: (firstBuy: bigint, lockFee: bigint, creationFee: bigint) =>
      `First buy ${formatBnbExact(firstBuy)} + lock fee ${formatBnbExact(lockFee)} + creation fee ${formatBnbExact(creationFee)} = ${formatBnbExact(firstBuy + lockFee + creationFee)} BNB sent`,
    gas: (wei: bigint) => `Network fee ≈ ${formatBnb(wei)} BNB, paid to the network, not to CADINU.`,
    gasUnknown: 'Your wallet will show the network fee.',
    fees: 'Fees after launch',
    feesLine: (fee: string) =>
      `Every trade in the CAD1 pool pays ${fee}. The protocol takes about a third (32 % by default); the rest goes to the liquidity in range: your locked position, plus anyone who adds liquidity to this pool.`,
    protection: 'Protection in your transaction',
    /** the contract refuses any change of the lock fee (exact value), so "changes from", not "rises above"; no 0-token clause without a buy;
     * no fee figure guessed while it is unread (review M9, H11) */
    protectionLine: (maxLockFee: bigint | undefined, maxCreationFee: bigint | undefined, deadline: string, minOut: string | null) => {
      const parts = [
        'if the start value changes',
        typeof maxLockFee === 'bigint' ? `if the lock fee changes from ${formatBnbExact(maxLockFee)} BNB` : 'if the lock fee changes from the value read at Review',
        typeof maxCreationFee === 'bigint' ? `if the creation fee changes from ${formatBnbExact(maxCreationFee)} BNB` : 'if the creation fee changes from the value read at Review',
        `if it is not confirmed by ${deadline}`,
      ]
      if (minOut) parts.push(`if your first buy would give fewer than ${minOut} tokens`)
      return `Refused ${parts.slice(0, -1).join(', ')}, or ${parts[parts.length - 1]}. In each case only the network fee is spent.`
    },
    deadlineLater: 'about 20 minutes after this review was read',
    raw: 'Show the raw call',
    rawPending: 'set when you press Launch',
    risksTitle: 'All risks',
    changedField: {
      paused: 'Launches paused',
      launchesOpen: 'Launches open',
      lockPrice: 'Lock fee',
      creationFee: 'Creation fee',
      minFirstBuy: 'Minimum first buy',
      startTick: 'Start step',
      startValue: 'Start value',
      /** under "You get": short, so "Your tokens: 9.9M → 8.2M" stays on one line at 360 px (r214 at 390 wrapped "8.2M" alone) */
      tokensOut: 'Your tokens',
    } as Record<string, string>,
    startMoved: 'The start price moved',
    noBnb: (need: bigint, have: bigint) => `Not enough BNB: this needs about ${formatBnb(need)} BNB with the network fee, and the wallet holds ${formatBnb(have)} BNB.`,
  },

  tx: {
    title: 'Your launch',
    loading: 'Reading BNB Chain',
    launching: (symbol: string | null) => (symbol ? `Launching $${symbol}` : 'Launching your token'),
    waiting: 'Waiting for BNB Chain',
    /** the label under Pending's one number: the time left before the signed deadline (audit 2026-10-09) */
    left: 'until it times out',
    timedOut: 'Timed out',
    pendingInfoTitle: 'Launching',
    pendingInfo: (deadline: bigint | undefined) =>
      deadline ? `You can close this page. If BNB Chain hasn't confirmed by ${humanTime(deadline)}, the launch is refused automatically.` : 'You can close this page. This link keeps working.',
    replaced: 'Replaced. Following the new one',
    replacedInfo: 'Your wallet replaced the transaction (a speed-up). This page follows the new one.',
    refusedBy: (deadline: bigint) => `Refused automatically if not confirmed by ${humanUtc(deadline)}.`,
    stepsCaption: 'One transaction: all of it happens, or none of it.',
    hashTitle: 'Transaction',
    cancelled: 'Cancelled in your wallet. Nothing launched.',
    notFound: 'BNB Chain does not know this transaction. Your wallet may not have sent it, or it replaced it with another one.',
    /** what came back, from the transaction's own value; nothing when it carried no BNB (review M8) */
    revertedValue: (value: bigint) => (value > 0n ? `The ${formatBnbExact(value)} BNB you sent came back with the refusal.` : ''),
    foreign: 'This transaction is not a CAD1 launch.',
    unknown: "Couldn't read this transaction just now. It may still land.",
    titles: { reverted: 'Not launched', cancelled: 'Cancelled', foreign: 'Not a CAD1 launch', notfound: 'Transaction not found', unknown: 'Not read yet', created: 'Launched' },
    /** a launch found by the launcher's count since the send, when the wallet's hash does not carry it (a smart account's own hash; 2026-10-10) */
    created: "Your wallet sent the launch through its own account, so this hash doesn't carry it. The launcher registered your token after you sent it.",
    createdLine: (symbol: string, at: bigint | null) => `$${symbol || 'CAD1'}${at ? `, launched on ${humanDate(at)}` : ''}`,
    /** the sender's next step after a refusal: back to the form, the draft restored (one line beside "Details" at 360 px) */
    editRetry: 'Try again',
    found: 'Found a launch from this wallet',
    foundInfo: (symbol: string, at: bigint) => `$${symbol}, launched on ${humanDate(at)}.`,
    openIt: 'Open it',
    lastTx: 'Your latest launch is another',
    lastTxInfo: "This browser's latest launch transaction is another one.",
    bscscan: 'BscScan',
    copyLink: 'Copy link',
    live: (symbol: string) => `$${symbol} is live on CADINUdex`,
    liveThird: (symbol: string) => `$${symbol} was launched on CADINUdex`,
    by: (creator: string) => `by ${creator}`,
    launchNo: (n: bigint) => `Launch #${n}`,
    lockedFor: `Locked ${LOCK_TERM}`,
    liveStamp: 'LIVE',
    proof: (unlockAt: bigint) => `Liquidity locked in the CADINU Lock until ${humanDate(unlockAt)}. Anyone can check it.`,
    certificate: 'Certificate',
    cardToken: 'Token page',
    cardTrade: 'Trade',
    cardCompetition: 'Competition',
    cardTradeSub: 'Swap, preset to your token',
    cardTradeSubThird: 'Swap, preset to this token',
    firstBuyLine: (bnb: bigint, tokens: bigint, symbol: string, bps: bigint, usd: string | null) =>
      `First buy ${formatBnb(bnb)} BNB → ${formatTokens(tokens)} $${symbol} (${formatShareBps(bps)} of supply)${usd ? ` · ≈ ${usd}` : ''}`,
    noBuyDone: 'No first buy: the creator started with 0 tokens; the whole supply went into the pool.',
    /** the share twins read "Share" beside the platform's icon (one line each at 360 px); these are their accessible names */
    share: 'Share',
    shareX: 'Share on X',
    shareTg: 'Share on Telegram',
    shareText: (symbol: string, unlockAt: bigint, url: string) => `I launched $${symbol} on CAD1. Liquidity locked ${LOCK_TERM}, until ${humanDate(unlockAt)}, in the CADINU Lock: ${url}`,
    nextCompetition: (symbol: string) => `Start a trading competition for $${symbol}`,
    nextFees: 'Collect your fees',
    nextWallet: (symbol: string) => `Add $${symbol} to your wallet`,
    tokenName: 'Token name',
    infoTitle: 'Logo, description and links',
    infoNone: 'None were given for this token.',
    infoMissing: 'Given at launch. Their fingerprint is in the token; the details stay with the creator until saving opens.',
    infoPosting: 'Saving them on CADINU…',
    infoPublished: 'Saved. They show on the token page, in the list and in Swap.',
    infoPublishedAlready: 'Already saved. They show on the token page, in the list and in Swap.',
    infoLogoGone: 'The logo file is not in this browser any more. Choose the same file again to save the page. The launch itself is complete.',
    infoFailed: 'Not saved yet. The launch is complete; they stay in this browser.',
    infoClosed: 'Saving is paused for now. The launch is complete; they stay in this browser.',
    infoRetry: 'Try again',
    infoPickLogo: 'Choose the logo file you used at launch:',
    lag: 'It can take a minute to appear in the launches list. The token page reads the chain directly.',
    detailsTitle: 'Launch details',
  },

  list: {
    title: 'Launches',
    info: "Newest first; CADINU doesn't review launches. Market value is the price × the whole supply, not money in the pool.",
    tabs: ['New', 'Active 24h', 'Mine'],
    search: 'Search name, symbol or address',
    searchLabel: 'Search launches',
    empty: 'No launches yet',
    emptyInfo: 'CAD1 opens when the launcher is deployed.',
    emptyCta: 'Launch the first one',
    mineEmpty: 'No launches from you yet',
    activeEmpty: 'No trades in 24 hours',
    noMatch: 'No match',
    error: "Can't read launches",
    more: 'Show more',
    lockedUntil: (unlockAt: bigint) => `Locked until ${year(unlockAt)}`,
    /** past the unlock date of the Launched event: a fact of the clock, never a lock state (the lock is not read on these pages) */
    lockPassed: 'Unlock date passed',
    launchNo: (n: number | bigint) => `#${n}`,
    marketValue: 'Market value (price × 1,000,000,000 supply)',
    /** the label over a row's one number (review H1, audit 2026-10-09) */
    marketValueShort: 'Market value',
    poolBnb: (wei: bigint) => `${formatBnb(wei)} BNB in pool`,
    /** "5 min", "3 h", "1 d" */
    age: (seconds: number) => {
      if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))} min`
      if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h`
      return `${Math.floor(seconds / 86_400)} d`
    },
    valueBnb: (wei: bigint) => `≈ ${bnbTenths(wei)} BNB`,
    detailsMissing: 'Details not read yet',
  },

  band: {
    title: 'Built on CAD1',
    sheet: 'What to know',
    /** the band's (i): a sentence or two and its "All risks" link; the operator sentence and the trader risks are in Details */
    sheetInfo: "CADINU doesn't review launches. Locked liquidity doesn't stop anyone from selling, and you can lose what you put in.",
    launchLine: (n: bigint | null, at: bigint) => `${n ? `Launch #${n} · ` : ''}${humanDate(at)}`,
    /** the band chip: what the creator wallet (the launch lock's owner) holds now, as a whole percent */
    holdsAt: (bps: bigint) => `Creator holds ${wholePercent(bps)}`,
    creator: 'Creator',
    /** same words as the chip below 1 % (the chip said "< 1 %" while Details said "0.00 %", 2026-10-09): one fact, one phrasing */
    holdsNow: (bps: bigint) => `Creator wallet holds ${bps < 100n ? wholePercent(bps) : formatShareBps(bps)} now`,
    lockUntil: (unlockAt: bigint) => `Locked until ${humanDate(unlockAt)}`,
    priceSource: 'Price and value on this page should be read from the CAD1 pool; other pools for this token were made by others.',
    poolLink: 'CAD1 pool',
    priceNow: 'Price now',
    report: 'Report',
    fake: 'Not a CAD1 launch',
    fakeInfo: 'Not in the CAD1 registry. A …CAD1 ending proves nothing.',
    poolBnb: 'BNB in the CAD1 pool',
    checkFailed: "Can't check CAD1 status",
    detailsFailed: "Can't read launch details",
    detailsTitle: 'CAD1 launch',
    /** shown under the operator sentence in the band's Details (review H12: the operator sentence is its own paragraph, never in this list) */
    traderRisks: [0, 2, 9, 13],
  },
} as const

/** Every string a test can see: COPY walked, functions called with sample arguments. */
export function allCopyStrings(): string[] {
  const out: string[] = []
  const samples: unknown[] = [10n ** 17n, 2_490_000_000_000_000_000n, 'TDOG', 1_800_000_000n]
  const walk = (v: unknown) => {
    if (typeof v === 'string') out.push(v)
    else if (Array.isArray(v)) v.forEach(walk)
    else if (typeof v === 'function') {
      try {
        const args = Array.from({ length: v.length }, (_, i) => (typeof samples[i] === 'bigint' ? 10n ** 17n : samples[i]))
        const r = v(...args)
        if (typeof r === 'string') out.push(r)
      } catch {
        // a template that needs other arguments: its literal parts are checked by the source scan
      }
    } else if (v && typeof v === 'object') Object.values(v).forEach(walk)
  }
  walk(COPY)
  out.push(OPERATOR_SENTENCE, startValueLine(9_916_373_456_926_851_492n, '$5,900'), feeShareLine(), feeShareLine(209_718_400, '0.5 %'), feeShareLine(0, '0.5 %'))
  out.push(...[0n, 3n * 10n ** 16n, undefined].flatMap((f) => Object.values(lockFeeWords(f)).filter((x): x is string => typeof x === 'string')))
  out.push(...[0n, 5n * 10n ** 15n, undefined].flatMap((f) => Object.values(creationFeeWords(f)).filter((x): x is string => typeof x === 'string')))
  out.push(compactAmount(9_934_230n * 10n ** 18n), compactUsd('$5,900') ?? '', wholePercent(99n), humanDate(2_106_848_000), humanUtc(2_106_848_000), humanTime(2_106_848_000))
  return out
}
